import { useEffect, useState } from 'react';

/**
 * In-memory cache for preloaded images
 * Uses browser's built-in image cache + memory cache for current session
 */
const imageCache = new Map<string, HTMLImageElement>();
const loadingPromises = new Map<string, Promise<HTMLImageElement>>();

/**
 * Preload an image and store it in memory cache
 */
const preloadImage = (src: string): Promise<HTMLImageElement> => {
  // Return from cache if already loaded
  if (imageCache.has(src)) {
    return Promise.resolve(imageCache.get(src)!);
  }

  // Return existing loading promise if already loading
  if (loadingPromises.has(src)) {
    return loadingPromises.get(src)!;
  }

  // Start loading
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      imageCache.set(src, img);
      loadingPromises.delete(src);
      resolve(img);
    };
    
    img.onerror = () => {
      loadingPromises.delete(src);
      reject(new Error(`Failed to load image: ${src}`));
    };
    
    img.src = src;
  });

  loadingPromises.set(src, promise);
  return promise;
};

/**
 * Hook to preload and cache an image
 * Returns loading state and any errors
 */
export const useImageCache = (src: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [cached, setCached] = useState(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      setCached(false);
      setError(null);
      return;
    }

    // Check if already in cache
    if (imageCache.has(src)) {
      setCached(true);
      setLoading(false);
      setError(null);
      return;
    }

    setCached(false);
    setLoading(true);
    setError(null);

    preloadImage(src)
      .then(() => {
        setCached(true);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [src]);

  return { loading, error, cached };
};

/**
 * Preload multiple images at once
 * Useful for preloading a page worth of images
 */
export const preloadImages = (urls: string[]): Promise<HTMLImageElement[]> => {
  return Promise.all(urls.map(preloadImage));
};

/**
 * Clear the in-memory image cache
 * Useful for forcing fresh loads or managing memory
 */
export const clearImageCache = () => {
  imageCache.clear();
  loadingPromises.clear();
};

/**
 * Get cache statistics
 */
export const getImageCacheStats = () => {
  return {
    cachedImages: imageCache.size,
    loadingImages: loadingPromises.size,
  };
};
